import { useCallback } from 'react';
import { Button, Collapse, Stack, Typography } from '@mui/material';

import { ChecklistStep } from './DashboardChecklist.interface';
import { ChecklistRow, ChevronToggle, StepIndicator, SubstepIndicator } from './DashboardChecklist.styles';

import CheckSmallIcon from 'modules/common/components/Icons/CheckSmallIcon';
import RotableChevron from 'modules/common/components/Icons/RotableChevron';

interface ChecklistStepRowProps {
    step: ChecklistStep;
    index: number;
    isExpanded: boolean;
    onToggle: (stepId: string) => void;
    onStart: (step: ChecklistStep) => void;
}

interface StepCtaProps {
    label: string;
    onClick: () => void;
}

function StepCta({ label, onClick }: Readonly<StepCtaProps>) {
    return (
        <Button variant="contained" color="brandPurple" size="large" onClick={onClick}>
            {label}
        </Button>
    );
}

function ChecklistStepRow({ step, index, isExpanded, onToggle, onStart }: Readonly<ChecklistStepRowProps>) {
    const handleStart = useCallback(() => onStart(step), [onStart, step]);
    const handleToggle = useCallback(() => onToggle(step.id), [onToggle, step.id]);

    const { completed, isReady, substeps } = step;
    const textColor = completed ? 'text.secondary' : 'text.primary';
    const hasSubsteps = substeps.length > 0;
    const canToggle = !completed && !isReady && hasSubsteps;
    const showSubsteps = !completed && hasSubsteps && (isReady || isExpanded);

    return (
        <ChecklistRow>
            <Stack direction="row" spacing={3} alignItems={isReady ? 'flex-start' : 'center'} sx={{ p: 3 }}>
                <StepIndicator completed={completed} data-testid={`step-indicator-${step.id}`}>
                    {completed ? (
                        <CheckSmallIcon fontSize="small" />
                    ) : (
                        <Typography variant="body1paragraph">{index + 1}</Typography>
                    )}
                </StepIndicator>
                <Stack spacing={3} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                    <Stack spacing={0.5}>
                        <Typography variant="labelSmall" color={textColor}>
                            {step.title}
                        </Typography>
                        <Typography variant="body1paragraph" color={textColor}>
                            {step.description}
                        </Typography>
                    </Stack>
                    {isReady && !hasSubsteps && <StepCta label={step.ctaLabel} onClick={handleStart} />}
                </Stack>
                {/* Substeps fill the space under the description, so the CTA moves beside it. */}
                {isReady && hasSubsteps && <StepCta label={step.ctaLabel} onClick={handleStart} />}
                {canToggle && (
                    <ChevronToggle
                        aria-label={`${isExpanded ? 'Hide' : 'Show'} ${step.title} steps`}
                        aria-expanded={isExpanded}
                        onClick={handleToggle}>
                        <RotableChevron isRotated={isExpanded} color="inherit" />
                    </ChevronToggle>
                )}
            </Stack>
            <Collapse in={showSubsteps} unmountOnExit>
                <Stack spacing={3} sx={{ pb: 2, pl: 10.5, pr: 3 }}>
                    {substeps.map((substep) => (
                        <Stack key={substep.id} direction="row" spacing={2} alignItems="center">
                            <SubstepIndicator
                                completed={substep.completed}
                                data-testid={`substep-indicator-${substep.id}`}
                                aria-label={substep.completed ? 'Completed' : 'Not started'}>
                                {substep.completed && <CheckSmallIcon fontSize="small" />}
                            </SubstepIndicator>
                            <Stack sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="bodySemiBold">{substep.title}</Typography>
                                {substep.description && (
                                    <Typography variant="body1paragraph">{substep.description}</Typography>
                                )}
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Collapse>
        </ChecklistRow>
    );
}

export default ChecklistStepRow;
